import { useCallback, useEffect, useRef, useState } from 'react';
import {
  BeforeInstallPromptEvent,
  dismissInstallPrompt,
  isIosDevice,
  isMobileDevice,
  isStandaloneDisplay,
  wasInstallPromptDismissed,
} from '../utils/pwaInstall';

const SHOW_DELAY_MS = 2200;

export type PwaInstallMode = 'native' | 'ios' | null;

export function usePwaInstall() {
  const deferredRef = useRef<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const [mode, setMode] = useState<PwaInstallMode>(null);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    if (isStandaloneDisplay() || wasInstallPromptDismissed()) return;

    const tryShow = () => {
      if (isStandaloneDisplay() || wasInstallPromptDismissed()) return;
      if (deferredRef.current) {
        setMode('native');
        setVisible(true);
        return;
      }
      if (isIosDevice() && isMobileDevice()) {
        setMode('ios');
        setVisible(true);
      }
    };

    const onBip = (e: Event) => {
      e.preventDefault();
      deferredRef.current = e as BeforeInstallPromptEvent;
      setMode('native');
      window.setTimeout(tryShow, 800);
    };

    window.addEventListener('beforeinstallprompt', onBip);
    const timer = window.setTimeout(tryShow, SHOW_DELAY_MS);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBip);
      window.clearTimeout(timer);
    };
  }, []);

  const dismiss = useCallback(() => {
    dismissInstallPrompt();
    setVisible(false);
  }, []);

  const install = useCallback(async () => {
    const ev = deferredRef.current;
    if (!ev) return;
    setInstalling(true);
    try {
      await ev.prompt();
      const { outcome } = await ev.userChoice;
      if (outcome === 'accepted') {
        setVisible(false);
        deferredRef.current = null;
      }
    } catch {
      /* user cancelled or browser blocked */
    } finally {
      setInstalling(false);
    }
  }, []);

  return {
    visible,
    mode,
    installing,
    canNativeInstall: Boolean(deferredRef.current),
    dismiss,
    install,
  };
}
